import {
  isCrossBorderResident,
  isHotelQuarantinePermit,
  isReturnExemption,
  isVictorianReturningFromRedZone,
  isFullVaxERZVictorian
} from './travel-reasons';
import { useEffect } from 'react';
import type { DeepPartial } from 'types/globals';
import { EmptyAddress, Values } from './initialValues';

type SetFieldValueFn = (field: string, value: any, shouldValidate?: boolean | undefined) => void;
type SetDeltaValueFn = (values: DeepPartial<Values>) => void;

const useFromQuarantineHotel = (values: Values, setFieldValue: SetFieldValueFn) => {
  const {
    assess: {
      Depart: {
        quarantine: { fromQuarantine }
      }
    }
  } = values;

  useEffect(() => {
    if (!fromQuarantine) {
      setFieldValue('assess.Depart.quarantine.hotel', '', false);
    } else {
      setFieldValue('assess.Depart.from', 'other', false);
    }
  }, [fromQuarantine, setFieldValue]);

  //   useEffect(() => {
  //     // Force the fromQuarantine flag to be true if the permit is hotel quarantine permit.
  //     // Otherwise we will set it to false to hide the quarantine hotel name input field from FE
  //     if (isHotelQuarantinePermit(reasonCode)) {
  //       setFieldValue('assess.Depart.quarantine.fromQuarantine', true, false);
  //     } else if (fromQuarantine) {
  //       setFieldValue('assess.Depart.quarantine.fromQuarantine', false, false);
  //     }
  //   }, [fromQuarantine, reasonCode, setFieldValue]);
};

const useCheckInvalidDestination = (values: Values, setFieldValue: SetFieldValueFn) => {
  const {
    assess: {
      Depart: {
        returningVicInvalidDestination,
        returningVicInvalidHome,
        target,
        homeAddress,
        homeAddressIsDestination
      },
      reasons: reasonCode
    }
  } = values;

  const homeState = homeAddress?.fullDetails.state ?? '';
  const homePostCode = homeAddress?.fullDetails.postCode ?? '';

  useEffect(() => {
    if (
      isVictorianReturningFromRedZone(reasonCode) ||
      isReturnExemption(reasonCode) ||
      isFullVaxERZVictorian(reasonCode)
    ) {
      if (
        !returningVicInvalidDestination &&
        ((target.fullDetails.state !== 'VIC' && target.fullDetails.state !== '') ||
          (!target.fullDetails.postCode.startsWith('3') && target.fullDetails.postCode !== ''))
      ) {
        // Incorrect values for Vic returning home
        setFieldValue('assess.Depart.returningVicInvalidDestination', true, false);
      } else if (
        returningVicInvalidDestination &&
        (target.fullDetails.state === 'VIC' || target.fullDetails.state === '') &&
        (target.fullDetails.postCode.startsWith('3') || target.fullDetails.postCode === '')
      ) {
        // Corrected values or cleared incorrect values
        setFieldValue('assess.Depart.returningVicInvalidDestination', false, false);
      }
      if (
        !returningVicInvalidHome &&
        ((homeState !== 'VIC' && homeState !== '') ||
          (!homePostCode.startsWith('3') && homePostCode !== ''))
      ) {
        // Incorrect values for Vic returning home
        setFieldValue('assess.Depart.returningVicInvalidHome', true, false);
      } else if (
        returningVicInvalidHome &&
        (homeState === 'VIC' || homeState === '') &&
        (homePostCode.startsWith('3') || homePostCode === '')
      ) {
        // Corrected values or cleared incorrect values
        setFieldValue('assess.Depart.returningVicInvalidHome', false, false);
      }
    } else if (returningVicInvalidDestination && returningVicInvalidHome) {
      // Deselect 'returning from vic' - clear both error if enabled
      setFieldValue('assess.Depart.returningVicInvalidDestination', false, false);
      setFieldValue('assess.Depart.returningVicInvalidHome', false, false);
    } else if (returningVicInvalidDestination) {
      // Deselect 'returning from vic' - clear returningVicInvalidDestination error if enabled
      setFieldValue('assess.Depart.returningVicInvalidDestination', false, false);
    } else if (returningVicInvalidHome) {
      // Deselect 'returning from vic' - clear returningVicInvalidHome error if enabled
      setFieldValue('assess.Depart.returningVicInvalidHome', false, false);
    }
  }, [
    reasonCode,
    target.fullDetails.state,
    target.fullDetails.postCode,
    homeState,
    homePostCode,
    returningVicInvalidDestination,
    returningVicInvalidHome,
    homeAddressIsDestination,
    setFieldValue
  ]);
};

const useHomeAddressIsDestinationCheckbox = (values: Values, setDeltaValues: SetDeltaValueFn) => {
  const {
    assess: {
      Depart: { target, homeAddressIsDestination }
    }
  } = values;

  useEffect(() => {
    if (homeAddressIsDestination && target.fullDetails.fullAddress !== '') {
      // Clear all destination fields if user selects homeAddressIsDestination checkbox
      setDeltaValues({
        assess: {
          Depart: {
            // @ts-expect-error - Seems to be an error with DeepPartial.. It's functional otherwise
            target: EmptyAddress,
            targetPlace: '',
            returningVicInvalidDestination: false
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeAddressIsDestination, target.fullDetails.fullAddress]);
};

const useForceHomeAddressForVictoria = (values: Values, setFieldValue: SetFieldValueFn) => {
  const {
    assess: {
      Depart: { homeAddressCountry },
      reasons: reasonCode
    }
  } = values;

  useEffect(() => {
    if (isVictorianReturningFromRedZone(reasonCode) && homeAddressCountry !== 'AUS') {
      setFieldValue('assess.Depart.homeAddressCountry', 'AUS', false);
    }
  }, [reasonCode, homeAddressCountry, setFieldValue]);
};

const useForceDepartFromOtherForVicRedzone = (values: Values, setFieldValue: SetFieldValueFn) => {
  const {
    assess: {
      Depart: { from },
      reasons: reasonCode
    }
  } = values;

  useEffect(() => {
    if (
      (isVictorianReturningFromRedZone(reasonCode) ||
        isReturnExemption(reasonCode) ||
        isFullVaxERZVictorian(reasonCode)) &&
      from !== 'other'
    ) {
      setFieldValue('assess.Depart.from', 'other', false);
    }
  }, [reasonCode, from, setFieldValue]);
};

const useForceHotelQuarantineFromAus = (values: Values, setFieldValue: SetFieldValueFn) => {
  const {
    assess: {
      Depart: { fromAddressCountry },
      reasons: reasonCode
    }
  } = values;

  useEffect(() => {
    if (isHotelQuarantinePermit(reasonCode) && fromAddressCountry !== 'AUS') {
      setFieldValue('assess.Depart.fromAddressCountry', 'AUS', false);
      setFieldValue('assess.Depart.fromAddress', EmptyAddress, false);
    }
  }, [fromAddressCountry, reasonCode, setFieldValue]);
};

const useEnsureDestinationOnUntoggleHomeAddress = (
  values: Values,
  setDeltaValues: SetDeltaValueFn
) => {
  const {
    assess: {
      Depart: { from, homeAddressIsDestination }
    }
  } = values;

  if (from === 'home' && homeAddressIsDestination) {
    setDeltaValues({
      assess: {
        Depart: {
          homeAddressIsDestination: false,
          // @ts-expect-error - Seems to be an error with DeepPartial.. It's functional otherwise
          homeAddress: EmptyAddress
        }
      }
    });
  }
};

const useCrossBorderHomeAddress = (values: Values, setFieldValue: SetFieldValueFn) => {
  const {
    assess: {
      reasons: reasonCode,
      Depart: { from }
    }
  } = values;

  useEffect(() => {
    if (isCrossBorderResident(reasonCode) && from !== 'other') {
      setFieldValue('assess.Depart.from', 'other', false);
    }
  }, [from, reasonCode, setFieldValue]);
};

export default function useAssessStepConstraints(
  values: Values,
  setFieldValue: SetFieldValueFn,
  setDeltaValues: SetDeltaValueFn
) {
  useFromQuarantineHotel(values, setFieldValue);
  useCheckInvalidDestination(values, setFieldValue);
  useHomeAddressIsDestinationCheckbox(values, setDeltaValues);
  useForceHomeAddressForVictoria(values, setFieldValue);
  useForceDepartFromOtherForVicRedzone(values, setFieldValue);
  useForceHotelQuarantineFromAus(values, setFieldValue);
  useEnsureDestinationOnUntoggleHomeAddress(values, setDeltaValues);
  useCrossBorderHomeAddress(values, setFieldValue);
}
